import { useEffect, useRef, useState } from "react";
import { FlagCountResponse, GroupCount, TopicCount } from "../../api/Items";
import {
  useFetchFlagCount,
  useFetchKeywordsCount,
  useFetchTopicsCount,
} from "../../api/ItemsApi";

type BaselineStatus = "loading" | "ready" | "error";

// After the unfiltered request fails, keep the provider totals for the option
// list. While filters are empty those totals are unfiltered, so later filtered
// responses must not replace them.
export function useListedCount<T>(
  baseline: T | undefined,
  baselineError: boolean,
  live: T | undefined,
  filtersEmpty: boolean
): T | undefined {
  const captured = useRef<T>();
  if (baseline) {
    captured.current = undefined;
    return baseline;
  }
  if (!baselineError) {
    captured.current = undefined;
    return undefined;
  }
  if (filtersEmpty && live) {
    captured.current = live;
  }
  return captured.current ?? live;
}

// Option lists need the unfiltered totals. Those requests stay on their own
// mutations so a filtered count written into the report provider cannot be
// saved as the baseline, even if it arrives first.
export function useUnfilteredCountBaseline(reportUuid: string | undefined) {
  const [trackedUuid, setTrackedUuid] = useState(reportUuid);
  const [status, setStatus] = useState<BaselineStatus>(
    reportUuid ? "loading" : "ready"
  );
  const [baselineFlagCount, setBaselineFlagCount] =
    useState<FlagCountResponse>();
  const [baselineKeywordCount, setBaselineKeywordCount] =
    useState<GroupCount>();
  const [baselineTopicCount, setBaselineTopicCount] = useState<TopicCount>();
  const { mutate: getBaselineFlags } = useFetchFlagCount();
  const { mutate: getBaselineKeywords } = useFetchKeywordsCount();
  const { mutate: getBaselineTopics } = useFetchTopicsCount();

  if (reportUuid !== trackedUuid) {
    setTrackedUuid(reportUuid);
    setBaselineFlagCount(undefined);
    setBaselineKeywordCount(undefined);
    setBaselineTopicCount(undefined);
    setStatus(reportUuid ? "loading" : "ready");
  }

  useEffect(() => {
    if (!reportUuid) return;
    let cancelled = false;
    let pending = 3;
    let failed = false;
    const finish = (ok: boolean) => {
      if (!ok) failed = true;
      pending -= 1;
      if (cancelled || pending > 0) return;
      setStatus(failed ? "error" : "ready");
    };
    getBaselineFlags(
      { report_uuid: reportUuid },
      {
        onSuccess: (data) => {
          if (!cancelled) setBaselineFlagCount(data);
          finish(true);
        },
        onError: () => finish(false),
      }
    );
    getBaselineKeywords(
      { report_uuid: reportUuid },
      {
        onSuccess: (data) => {
          if (!cancelled) setBaselineKeywordCount(data);
          finish(true);
        },
        onError: () => finish(false),
      }
    );
    getBaselineTopics(
      { report_uuid: reportUuid },
      {
        onSuccess: (data) => {
          if (!cancelled) setBaselineTopicCount(data);
          finish(true);
        },
        onError: () => finish(false),
      }
    );
    return () => {
      cancelled = true;
    };
  }, [getBaselineFlags, getBaselineKeywords, getBaselineTopics, reportUuid]);

  return {
    baselineFlagCount,
    baselineKeywordCount,
    baselineTopicCount,
    baselineLoading: status === "loading",
    baselineError: status === "error",
  };
}
